import axios from 'axios'
import i18n from 'i18next';

// La cookie csrf_token se emite NO httpOnly a propósito para leerla por JS y
// mandarla como header X-Csrf-Token (doble-submit). En producción llega
// host-only a backend.smartquick.com.co, así que desde otro subdominio
// (creacionservicios, pwa) document.cookie no la ve nunca. Por eso el back la
// devuelve también en el body de login/cambio de empresa/MFA/session-info, y
// se guarda de respaldo. Se prefiere la cookie cuando sí es legible.
const CSRF_FALLBACK_KEY = 'csrf_token_fallback'

const getCookieValue = (name) => {
	const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'))
	return match ? decodeURIComponent(match[1]) : null
}

const getCsrfToken = () => getCookieValue('csrf_token') || getCookieValue('csrftoken') || sessionStorage.getItem(CSRF_FALLBACK_KEY)

// Cuando la sesión se recupera por cookie (recarga de página o pestaña nueva)
// nunca se pasa por el login, así que no hay respaldo guardado y la cookie no
// es legible: sin esto toda request mutante daría 403. /users/session/info/
// devuelve csrf_token en el cuerpo y el interceptor de respuesta lo guarda.
let csrfRefresh = null
const refreshCsrfToken = () => {
	if (!csrfRefresh) {
		csrfRefresh = axios.get('users/session/info/')
			.catch(() => null)
			.finally(() => { csrfRefresh = null })
	}
	return csrfRefresh
}

let _companyId = null;
let _token = null; // Token en bruto (string) para el header Authorization

const HTTP = {
	init() {
		axios.defaults.baseURL = process.env.REACT_APP_API_URL
		axios.defaults.withCredentials = true

		// Interceptor de PETICIÓN
		axios.interceptors.request.use(config => {
			// 1. CSRF Token (X-Csrf-Token) — cookie si es legible, si no el respaldo
			const csrfToken = getCsrfToken()
			if (csrfToken) {
				config.headers['X-Csrf-Token'] = csrfToken
			}

			// 2. Idioma
			config.headers['language'] = i18n.language

			// 3. Content-Type (Solo para peticiones con cuerpo)
			if (['post', 'put', 'patch'].includes(config.method)) {
				if (!config.headers['Content-Type']) {
					config.headers['Content-Type'] = 'application/json'
				}
			}

			// 4. Authorization (Si existe token en memoria)
			if (_token && !config.isLogin) {
				config.headers['Authorization'] = `Bearer ${_token}`
			}

			// 4. Header de Company
			if (_companyId) {
				config.headers['company'] = _companyId
			}

			// 6. Anti-Cache (Solo parámetro, no headers que bloquean CORS)
			if (process.env.REACT_APP_DISABLE_CACHE === 'true') {
				config.params = { ...config.params, _t: Date.now() }
			}

			// 7. Sin csrf_token y con request mutante: se pide uno fresco antes
			// de seguir, en vez de mandarla derecho al 403. El login no aplica
			// (todavía no hay sesión) y los GET tampoco lo necesitan, así que
			// la propia llamada a session/info no puede reentrar aquí.
			if (!csrfToken && !config.isLogin && ['post', 'put', 'patch', 'delete'].includes(config.method)) {
				return refreshCsrfToken().then(() => {
					const fresh = getCsrfToken()
					if (fresh) {
						config.headers['X-Csrf-Token'] = fresh
					}
					return config
				})
			}

			return config
		}, error => Promise.reject(error))

		// Interceptor de RESPUESTA: guarda el csrf_token que venga en el cuerpo
		// (login, cambio de empresa, MFA, session/info), único punto donde
		// llega cuando la cookie no es legible.
		axios.interceptors.response.use(response => {
			if (response?.data?.csrf_token) {
				sessionStorage.setItem(CSRF_FALLBACK_KEY, response.data.csrf_token)
			}
			return response
		}, error => Promise.reject(error))
	},

	setCompanyId(id) {
		_companyId = id;
	},

	setToken(token) {
		_token = token;
	},

	all(requests) {
		return axios.all(requests)
	},

	get(resource, slug) {
		return axios.get(resource + (slug ? '/' + slug : ''))
	},

	getWithQuery(resource, params) {
		return axios.get(resource, { params })
	},

	getArchive(resource, params) {
		return axios.get(resource, { params, responseType: 'blob' })
	},

	getBlob(resource) {
		return axios.get(resource, { responseType: 'blob' })
	},

	getBlobWithParams(resource, params) {
		return axios.get(resource, { params, responseType: 'blob' })
	},

	post(resource, params, isLogin, isFileHeaders) {
		const config = { isLogin }
		if (isFileHeaders) {
			config.headers = { 
				'Accept': 'application/json',
				'Content-Type': 'multipart/form-data' 
			}
		}
		return axios.post(resource, params, config)
	},

	postBlob(resource, params) {
		return axios.post(resource, params, { responseType: 'blob' })
	},

	put(resource, params) {
		return axios.put(resource, params)
	},

	delete(resource, params) {
		return axios.delete(resource, params)
	},

	deleteWithBody(resource, data) {
		return axios.delete(resource, { data })
	},

	patch(resource, params) {
		return axios.patch(resource, params)
	},

	update(resource, slug, params) {
		return axios.put(`${resource}/${slug}`, params)
	},

	// Métodos para Sockets
	getSocketWithQuery(resource, params) {
		const url = (process.env.VUE_APP_URL_SOCKET || '') + resource
		return axios.get(url, { params })
	},

	getSocket(resource) {
		const url = (process.env.VUE_APP_URL_SOCKET || '') + resource
		return axios.get(url)
	},

	postSocket(resource, params) {
		const url = (process.env.VUE_APP_URL_SOCKET || '') + resource
		return axios.post(url, params)
	}
}

export default HTTP
